import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  Button,
  TextField,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  MenuItem
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import BanksTable from "./components/BanksTable";
import BankFormModal from "./components/BankFormModal";
import DeleteDialog from "./components/DeleteDialog";
import useBanks from "./services/useBankHook";

function Bank() {
  const navigate = useNavigate();

  const {
    banks,
    searchValue,
    setStatus,
    status,
    setSearchValue,
    currentPage,
    totalPages,
    loading,
    error,
    success,
    handlePageChange,
    handleStatusToggle,
    resetAlerts,
    setCurrentPage,
    handledeleteBank,
    saveBank,
    updateBankData,
  } = useBanks();

  const INITIAL_BANK_STATE = {
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
    branch_code: "",
    status: "active",
    address: "",
  };

  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bankData, setBankData] = useState(INITIAL_BANK_STATE);

  const handleSearchChange = (e) => {
    setCurrentPage(1);
    setSearchValue(e.target.value);
  };

  const handleOpenModal = () => {
    setBankData(INITIAL_BANK_STATE);
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setBankData(INITIAL_BANK_STATE);
    setIsEditing(false);
    setCurrentId(null);
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };




  const handleBankSubmit = (e) => {
    e.preventDefault();

    if (isEditing) {
      updateBankData(currentId, bankData); // UPDATE API
    } else {
      saveBank(bankData); // CREATE API
    }

    setOpenModal(false);
  };


  const handleEditClick = (id, bankData) => {
    setCurrentId(id);          // save ID
    setBankData({ ...bankData }); // 👈 THIS IS THE KEY
    setIsEditing(true);
    setOpenModal(true);
  };


  // const handleDeleteBank = (id) => {
  //   setCurrentId(id);
  //   setDeleteDialogOpen(true);
  // };


  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Banks
                </MDTypography>
              </MDBox>

              {/* Search */}
              <MDBox pt={2} px={2} display="flex" justifyContent="space-between" alignItems="center">
                {/* Left side: Search input */}
                <TextField
                  label="Search"
                  placeholder="Search bank name"
                  size="small"
                  sx={{ maxWidth: 360, width: "100%" }}
                  value={searchValue}
                  onChange={handleSearchChange}
                />

                {/* Right side: Status dropdown + Create Bank button */}
                <MDBox display="flex" alignItems="center" ml={2} gap={1}>
                  {/* Status dropdown */}
                  <TextField
                    select
                    size="small"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setCurrentPage(1); // reset to first page when filter changes
                    }}
                    sx={{
                      minWidth: 150,
                      "& .MuiSelect-select": {
                        color: "gray",      // text color
                        fontWeight: 500,    // medium weight
                        fontSize: 20,       // adjust size as needed
                      },
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>


                  {/* Create Bank button */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    style={{
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    Create Bank
                  </Button>
                </MDBox>
              </MDBox>




              {/* Table */}

              <BanksTable
                rows={banks}
                onToggleStatus={handleStatusToggle}
                editClick={handleEditClick}
                deleteClick={(id) => {
                  setCurrentId(id);          // store the id
                  setDeleteDialogOpen(true); // open confirmation dialog
                }}
              />


              {/* Pagination */}
              <MDBox my={2} display="flex" justifyContent="center" alignItems="center">
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                <MDTypography mx={2}>
                  Page {currentPage} of {totalPages}
                </MDTypography>

                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* Bank Form Modal */}
      <BankFormModal
        open={openModal}
        isEditing={isEditing}
        formData={bankData}
        // onClose={handleCloseModal}
        onChange={handleBankChange}
        onSubmit={handleBankSubmit}
        onClose={() => setOpenModal(false)}
      />

      {/* add delete modal */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={async () => {
          if (currentId) {
            handledeleteBank(currentId); // call your hook function to delete
            setDeleteDialogOpen(false);  // close dialog
            setCurrentId(null);           // reset selected id
          }
        }}
        title="Delete Bank"
        message="Are you sure you want to delete this bank?"
      />



      {/* Loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Alerts */}
      <Snackbar
        open={Boolean(error || success)}
        autoHideDuration={3000}
        onClose={resetAlerts}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={error ? "error" : "success"}
          onClose={resetAlerts}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Bank;
